import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { Translate, withLocalize } from 'react-localize-redux';
import Button from '../../components/atoms/Button';
import Icon, { IconSizes, IconTypes, IconVariants } from '../../components/foundations/Icon';
import {
    trackWatchlistEvent,
    TRANSLATION_KEY as WATCHLIST_TRANSLATION_KEY,
    WATCHLIST_EVENTS,
} from '../../watchlist/constants';
import useMonitorAlerts from '../hooks/useMonitorAlerts';
import { dismissBanner } from '../store';
import { TRANSLATION_KEY, WATCHLIST_PATH } from '../constants';
import { buildWatchlistHash, countKey } from '../utils';
import AlertTime from './AlertTime';
import css from './MonitorAlertsBanner.css';

const BANNER_KEY = `${TRANSLATION_KEY}.BANNER`;

const TOPBAR_HEIGHT = 50;

const setStyle = (element, property, value) => {
    if (value) element.style.setProperty(property, value);
    else element.style.removeProperty(property);
};

// Move the scrollports themselves, so sticky controls stay below the fixed banner.
const useBannerSpace = (visible, bannerRef) => {
    const [top, setTop] = useState(TOPBAR_HEIGHT);

    useLayoutEffect(() => {
        if (!visible) return undefined;
        const reactContainer = document.getElementById('reactContainer');
        const angularContainer = document.getElementById('content');
        const containers = [reactContainer, angularContainer].filter(Boolean);
        const properties = ['top', 'margin-top', 'height', 'transition'];
        const originals = containers.map(container =>
            properties.map(property => container.style.getPropertyValue(property))
        );
        const restore = () =>
            containers.forEach((container, index) => {
                properties.forEach((property, i) => setStyle(container, property, originals[index][i]));
            });

        const measure = () => {
            restore();
            containers.forEach(container => {
                container.style.transition = 'none';
            });
            const height = bannerRef.current?.offsetHeight || 0;
            if (reactContainer) {
                setTop(Math.round(reactContainer.getBoundingClientRect().top));
                const baseTop = parseFloat(window.getComputedStyle(reactContainer).top);
                reactContainer.style.top = `${baseTop + height}px`;
            }
            if (angularContainer) {
                const style = window.getComputedStyle(angularContainer);
                const marginTop = parseFloat(style.marginTop) || 0;
                const contentHeight = parseFloat(style.height);
                angularContainer.style.marginTop = `${marginTop + height}px`;
                if (Number.isFinite(contentHeight))
                    angularContainer.style.height = `${Math.max(0, contentHeight - height)}px`;
            }
        };
        measure();
        window.addEventListener('resize', measure);
        const observer = new MutationObserver(measure);
        containers.forEach(container => observer.observe(container, { attributes: true, attributeFilter: ['class'] }));
        const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        if (bannerRef.current) resizeObserver?.observe(bannerRef.current);

        return () => {
            window.removeEventListener('resize', measure);
            observer.disconnect();
            resizeObserver?.disconnect();
            restore();
        };
    }, [visible, bannerRef]);

    return top;
};

const MonitorAlertsBanner = ({ translate }) => {
    const { missedAttention, lastCheckAt, bannerDismissed } = useMonitorAlerts();
    const slim = useSelector(state => state.settings.isSidenavSlim);
    const visible = !bannerDismissed && missedAttention.length > 0;
    const bannerRef = useRef(null);
    const top = useBannerSpace(visible, bannerRef);

    const [newest] = missedAttention;
    const others = missedAttention.length - 1;
    const newestTitle = newest && (newest.title || translate(`${WATCHLIST_TRANSLATION_KEY}.CARD.UNTITLED`));
    const headlineData = others > 0 ? { title: newestTitle, count: others } : { title: newestTitle };

    const review = () => {
        trackWatchlistEvent(WATCHLIST_EVENTS.ALERTS_BANNER_REVIEW, { count: missedAttention.length });
        dismissBanner();
        window.location.hash = buildWatchlistHash(WATCHLIST_PATH, null);
    };

    // Mounted empty: assistive tech announces mutations of a region it was already
    // watching, not one inserted together with its first content.
    return (
        <div role="status" aria-live="polite">
            {visible && (
                <div ref={bannerRef} className={classNames(css.banner, { [css.slim]: slim })} style={{ top }}>
                    <Icon
                        type={IconTypes.TABLER}
                        name="bell"
                        size={IconSizes.X_SMALL}
                        variant={IconVariants.ACTIVE}
                        interactive={false}
                    />
                    <span className={css.headline}>
                        <Translate
                            id={`${BANNER_KEY}.${countKey('TITLE', missedAttention.length)}`}
                            data={headlineData}
                            options={{ renderInnerHtml: false }}
                        />
                    </span>
                    <span className={css.detail}>
                        {lastCheckAt && (
                            <span className={css.lastCheck}>
                                {/* Separate elements: neighbouring text runs coalesce into
                                    one anonymous flex item and the gap never applies. */}
                                <span>
                                    <Translate id={`${BANNER_KEY}.LAST_CHECK`} />
                                </span>
                                <span>
                                    <AlertTime lastRunAt={lastCheckAt} />
                                </span>
                            </span>
                        )}
                    </span>
                    <span className={css.spacer} />
                    <Button
                        type="flat"
                        level="level2"
                        className={css.actionButton}
                        text={`${BANNER_KEY}.REVIEW`}
                        onClick={review}
                    />
                    <button
                        type="button"
                        className={css.closeButton}
                        aria-label={translate(`${BANNER_KEY}.DISMISS`)}
                        onClick={dismissBanner}
                    >
                        <Icon
                            type={IconTypes.TABLER}
                            name="x"
                            size={IconSizes.XX_SMALL}
                            variant={IconVariants.LIGHT}
                            interactive={false}
                        />
                    </button>
                </div>
            )}
        </div>
    );
};

MonitorAlertsBanner.propTypes = {
    translate: PropTypes.func.isRequired,
};

export default withLocalize(MonitorAlertsBanner);
